import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import Logo from '@/components/Logo';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useSecureAuth } from '@/hooks/useSecureAuth';
import { validateEmail, validatePassword, validateName } from '@/lib/security';
import googleLogo from '@/assets/google-logo.png';

const AuthPage = () => {
  const { secureSignIn, secureSignUp, secureGoogleSignIn, isLoading: authLoading } = useSecureAuth();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    // Client-side validation
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      toast({
        title: "Invalid Email",
        description: emailValidation.error,
        variant: "destructive",
      });
      return;
    }

    if (!password || password.trim().length === 0) {
      toast({
        title: "Invalid Password",
        description: "Password is required",
        variant: "destructive",
      });
      return;
    }

    await secureSignIn(email, password);
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      toast({
        title: "Agreement Required",
        description: "Please agree to the Terms of Service and Privacy Policy to continue.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const firstName = formData.get('firstName') as string;

    // Client-side validation
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      toast({
        title: "Invalid Email",
        description: emailValidation.error,
        variant: "destructive",
      });
      return;
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      toast({
        title: "Weak Password",
        description: passwordValidation.error,
        variant: "destructive",
      });
      return;
    }

    if (firstName && firstName.trim().length > 0) {
      const nameValidation = validateName(firstName);
      if (!nameValidation.isValid) {
        toast({
          title: "Invalid Name",
          description: nameValidation.error,
          variant: "destructive",
        });
        return;
      }
    }

    await secureSignUp(email, password, firstName);
  };

  const handleGoogleSignIn = async () => {
    await secureGoogleSignIn();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center justify-center mb-8 space-y-4">
          <Logo size="xl" showText={false} />
          <h1 className="text-3xl font-bold bg-gradient-hero bg-clip-text text-transparent">
            SKECHA
          </h1>
        </div>

        <Card>
          <CardHeader className="text-center">
            <CardTitle>Welcome to SKECHA</CardTitle>
            <CardDescription>
              Create beautiful AI-generated coloring pages with ease
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin" data-testid="signin-tab">Sign In</TabsTrigger>
                <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
              </TabsList>
              
              <TabsContent value="signin">
                <div className="space-y-4">
                  <Button 
                    type="button"
                    onClick={handleGoogleSignIn}
                    variant="outline"
                    className="w-full bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 font-medium h-11"
                    disabled={authLoading}
                    data-testid="google-signin-button"
                    data-gtm-event="login"
                    data-gtm-method="google"
                  >
                    <img src={googleLogo} alt="" className="w-5 h-5 mr-3" />
                    Sign in with Google
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                    </div>
                  </div>
                </div>
                
                <form onSubmit={handleSignIn} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter your email"
                      required
                      disabled={authLoading}
                      data-testid="signin-email-input"
                      maxLength={320}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="Enter your password"
                      required
                      disabled={authLoading}
                      data-testid="signin-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    variant="hero" 
                    disabled={authLoading} 
                    data-testid="signin-button"
                    data-gtm-event="login"
                    data-gtm-method="password"
                  >
                    {authLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Sign In
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="signup">
                <div className="space-y-4">
                  <Button 
                    type="button"
                    onClick={handleGoogleSignIn}
                    variant="outline"
                    className="w-full bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 font-medium h-11"
                    disabled={authLoading}
                    data-testid="google-signup-button"
                    data-gtm-event="sign_up"
                    data-gtm-method="google"
                  >
                    <img src={googleLogo} alt="Google" className="w-5 h-5 mr-3" />
                    Sign up with Google
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                    </div>
                  </div>
                </div>
                
                <form onSubmit={handleSignUp} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder="Enter your first name"
                      disabled={authLoading}
                      data-testid="firstname-input"
                      maxLength={100}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter your email"
                      required
                      disabled={authLoading}
                      data-testid="signup-email-input"
                      maxLength={320}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="Create a password"
                      required
                      disabled={authLoading}
                      data-testid="signup-password-input"
                      minLength={8}
                    />
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="agree-terms"
                      checked={agreedToTerms}
                      onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                      disabled={authLoading}
                      data-testid="terms-checkbox"
                    />
                    <Label htmlFor="agree-terms" className="text-sm leading-5">
                      I agree to the{' '}
                      <Link to="/terms" className="text-primary hover:underline">
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" className="text-primary hover:underline">
                        Privacy Policy
                      </Link>
                    </Label>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full" 
                    variant="hero" 
                    disabled={authLoading || !agreedToTerms}
                    data-testid="signup-button"
                    data-gtm-event="sign_up"
                    data-gtm-method="email"
                  >
                    {authLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Sign Up & Get 12 Free Credits
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;